import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCanonical } from '@/hooks/useCanonical';
import { ArrowRight, Shield, Star, Sparkles, Users, CheckCircle2, Phone, Crown, Stethoscope, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { services, PLANITY_URL } from '@/data/services';
import { blogArticles } from '@/data/blog-articles';
import BlogCard from '@/components/blog/BlogCard';
import CTASection from '@/components/CTASection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const Services = () => {
  useCanonical('/services');
  useEffect(() => {
    document.title = 'Nos soins bien-être à Sevran (93) — Body by CC | Institut Santé & Esthétique';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Découvrez tous les soins proposés par Body by CC à Sevran (93) : HIFU, E-SLIM Cryo 360°, soins visage, épilation, pressothérapie, massages bien-être. Infirmière diplômée, technologies dernier cri.');
  }, []);

  const premiumServices = services.filter(s => s.premium);
  const otherServices = services.filter(s => !s.premium);

  const highlights = [
    { icon: Shield, title: 'Expertise médicale', desc: 'Carine Tchuente, infirmière diplômée, allie rigueur clinique et bienveillance pour des soins sûrs et efficaces.' },
    { icon: Users, title: 'Bien-être & confiance', desc: 'Une approche centrée sur le bien-être, l\'acceptation de soi et la confiance en son corps.' },
    { icon: Sparkles, title: 'Machines dernier cri', desc: 'HIFU, E-SLIM Cryo 360°, Skincleantech, LED : des équipements de pointe pour des résultats visibles.' },
    { icon: Star, title: 'Résultats dès la 1ère séance', desc: 'Des protocoles éprouvés pour des résultats immédiats et durables, adaptés à vos objectifs.' },
  ];

  return (
    <>
      {/* Hero */}
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="max-w-7xl mx-auto relative">
          <span className="text-xs font-heading font-medium text-accent uppercase tracking-[0.2em]">Nos expertises</span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold mt-4 leading-tight">Bien-être, confiance & beauté</h1>
          <p className="mt-5 text-petrol-foreground/60 max-w-2xl text-base md:text-lg leading-relaxed font-body">
            HIFU, E-SLIM Cryo 360°, soins visage, épilation, massages et plus encore.
            Des soins réalisés par une <strong className="text-petrol-foreground/80">infirmière diplômée</strong> avec des machines dernier cri, dans une démarche de bien-être global.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-heading font-medium text-base hover:bg-primary/90 transition-colors"
            >
              Prendre rendez-vous <ArrowRight size={16} />
            </a>
            <a href="tel:+33645230034" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-petrol-foreground/20 text-petrol-foreground font-heading font-medium text-base hover:bg-petrol-foreground/10 transition-colors">
              <Phone size={16} /> 06 45 23 00 34
            </a>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="px-6 lg:px-8 -mt-8 relative z-10 pb-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {highlights.map((h, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="flex items-start gap-3 p-5 rounded-2xl border border-border bg-card shadow-lg"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <h.icon size={18} className="text-primary" />
              </div>
              <div>
                <p className="font-heading font-bold text-sm text-foreground">{h.title}</p>
                <p className="text-xs text-muted-foreground font-body mt-1 leading-relaxed">{h.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Soins premium */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3 flex items-center justify-center gap-2">
              <Crown size={14} /> Soins signature
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Nos technologies de pointe</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto font-body">
              Des équipements dernier cri pour des résultats exceptionnels. Ces soins haut de gamme sont notre spécialité.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {premiumServices.map((s, i) => (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="group block p-8 rounded-xl border-2 border-primary/20 bg-card hover:border-primary/50 hover:shadow-xl transition-all h-full relative overflow-hidden"
                >
                  <div className="absolute top-4 right-4">
                    <span className="text-[10px] font-heading font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary uppercase tracking-wider">Premium</span>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                    <s.icon size={22} className="text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-foreground">{s.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed font-body">{s.shortDesc}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground/60 font-body">{s.prestations.length} prestation{s.prestations.length > 1 ? 's' : ''}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary font-body">
                      Découvrir <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tous les autres soins */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Nos prestations</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Tous nos soins</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto font-body">
              Chaque soin est personnalisé après un diagnostic complet. Cliquez pour découvrir les tarifs et détails.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {otherServices.map((s, i) => (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="group block p-6 rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-lg transition-all h-full"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <s.icon size={18} className="text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground">{s.title}</h3>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed font-body">{s.shortDesc}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground/60 font-body">{s.prestations.length} prestation{s.prestations.length > 1 ? 's' : ''}</span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity font-body">
                      Voir <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Formules teaser */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Offres</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">Nos formules avantageuses</h2>
          <p className="text-muted-foreground font-body max-w-xl mx-auto mb-8">
            Combinez plusieurs soins à tarif préférentiel. Découverte visage, peau neuve, beauté express, corps sculpté et formules épilation.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {['Découverte Visage — 110 €', 'Peau Neuve — 110 €', 'Beauté Express — 70 €', 'Corps Sculpté — 60 €'].map((f) => (
              <span key={f} className="px-4 py-2 rounded-full bg-card border border-border text-sm font-body text-foreground">{f}</span>
            ))}
          </div>
          <Link
            to="/formules"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-heading font-medium text-base hover:bg-primary/90 transition-colors"
          >
            Voir toutes les formules <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Spécialité peaux noires */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-start gap-6 p-6 md:p-8 rounded-2xl border border-primary/20 bg-card"
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Users size={24} className="text-primary" />
            </div>
            <div className="space-y-3">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Spécialiste peaux noires & métissées</h2>
              <p className="text-muted-foreground leading-relaxed font-body">
                Body by CC est l'un des rares instituts de Seine-Saint-Denis à proposer des soins spécifiquement calibrés pour les peaux à phototypes foncés (III à VI).
                Carine Tchuente, infirmière diplômée, adapte chaque protocole pour garantir <strong>sécurité et bien-être</strong>.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2.5 text-sm"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Épilation définitive adaptée aux peaux foncées</span></li>
                <li className="flex items-start gap-2.5 text-sm"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Peelings adaptés contre l'hyperpigmentation</span></li>
                <li className="flex items-start gap-2.5 text-sm"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Protocoles anti-taches et unification du teint sécurisés</span></li>
                <li className="flex items-start gap-2.5 text-sm"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Prévention de l'hyperpigmentation post-inflammatoire</span></li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Notre approche bien-être */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Notre approche</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Le bien-être avant tout</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="p-6 rounded-xl border border-border bg-background">
              <Stethoscope size={20} className="text-primary mb-3" />
              <h3 className="font-heading font-semibold text-base text-foreground">Expertise médicale</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-body">
                Carine Tchuente est infirmière diplômée, formée en esthétique médicale à la Clinique des Champs-Élysées. Chaque soin est encadré par une rigueur médicale.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-background">
              <Heart size={20} className="text-primary mb-3" />
              <h3 className="font-heading font-semibold text-base text-foreground">Confiance en soi</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-body">
                Notre approche de l'esthétique n'est pas du camouflage. C'est un accompagnement bienveillant pour vous aider à vous sentir bien dans votre corps.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-background">
              <Sparkles size={20} className="text-primary mb-3" />
              <h3 className="font-heading font-semibold text-base text-foreground">Machines dernier cri</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-body">
                HIFU, E-SLIM Cryo 360°, Skincleantech, photothérapie LED, électro-mésothérapie : des équipements professionnels pour des résultats exceptionnels.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ des soins */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">FAQ</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Questions fréquentes</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                q: 'Comment se déroule un premier rendez-vous ?',
                a: (
                  <div className="space-y-4">
                    <p>Lors de votre premier rendez-vous, Carine réalise un <strong>diagnostic de peau complet et gratuit</strong>.</p>
                    <ol className="space-y-3">
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 font-heading font-bold text-xs text-primary">1</span>
                        <span><strong>Analyse de peau</strong> — Type de peau, problématiques, phototype</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 font-heading font-bold text-xs text-primary">2</span>
                        <span><strong>Discussion objectifs</strong> — Vos attentes et résultats souhaités</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 font-heading font-bold text-xs text-primary">3</span>
                        <span><strong>Protocole personnalisé</strong> — Plan de soins sur-mesure</span>
                      </li>
                    </ol>
                    <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 p-3">
                      <Sparkles size={16} className="text-accent shrink-0" />
                      <p className="text-sm"><strong>Diagnostic offert</strong> pour tout premier soin technique.</p>
                    </div>
                  </div>
                ),
              },
              {
                q: 'Vos soins sont-ils adaptés aux peaux noires et métissées ?',
                a: (
                  <div className="space-y-4">
                    <p><strong>Oui, c'est notre spécialité.</strong> Body by CC est l'un des rares instituts de Seine-Saint-Denis spécialisé dans les peaux à phototypes foncés.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Phototypes III à VI</strong> — Protocoles calibrés pour chaque carnation</span></li>
                      <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Anti-hyperpigmentation</strong> — Peelings et soins ciblés pour unifier le teint</span></li>
                      <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Épilation sécurisée</strong> — Technologies adaptées sans risque de brûlure</span></li>
                      <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Expertise infirmière</strong> — Carine est diplômée et formée en esthétique médicale</span></li>
                    </ul>
                  </div>
                ),
              },
              {
                q: 'Quelles sont vos technologies phares ?',
                a: (
                  <div className="space-y-4">
                    <p>Nos équipements dernier cri pour des résultats professionnels :</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="rounded-xl bg-primary/5 border border-primary/10 p-3">
                        <p className="font-heading font-bold text-sm text-foreground">HIFU</p>
                        <p className="text-xs text-muted-foreground mt-1">Lifting sans chirurgie par ultrasons focalisés. Résultats 12-18 mois.</p>
                      </div>
                      <div className="rounded-xl bg-primary/5 border border-primary/10 p-3">
                        <p className="font-heading font-bold text-sm text-foreground">E-SLIM Cryo 360°</p>
                        <p className="text-xs text-muted-foreground mt-1">Cryothérapie minceur. Jusqu'à -4 cm par séance.</p>
                      </div>
                      <div className="rounded-xl bg-primary/5 border border-primary/10 p-3">
                        <p className="font-heading font-bold text-sm text-foreground">Électro-Mésothérapie</p>
                        <p className="text-xs text-muted-foreground mt-1">Anti-âge avancé sans aiguille, infusion d'actifs.</p>
                      </div>
                      <div className="rounded-xl bg-primary/5 border border-primary/10 p-3">
                        <p className="font-heading font-bold text-sm text-foreground">Skincleantech</p>
                        <p className="text-xs text-muted-foreground mt-1">Nettoyage profond 3-en-1 : aspiration, exfoliation, infusion.</p>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                q: 'Comment prendre rendez-vous ?',
                a: (
                  <div className="space-y-4">
                    <p>Deux options simples et rapides :</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <a href={PLANITY_URL} target="_blank" rel="noopener noreferrer" className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center hover:border-primary/30 transition-colors block">
                        <Crown size={20} className="text-primary mx-auto mb-2" />
                        <p className="font-heading font-bold text-sm text-foreground">Planity</p>
                        <p className="text-xs text-muted-foreground mt-1">Réservez 24h/24 en ligne</p>
                      </a>
                      <a href="tel:+33645230034" className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center hover:border-primary/30 transition-colors block">
                        <Phone size={16} className="text-primary mx-auto mb-2" />
                        <p className="font-heading font-bold text-sm text-foreground">Téléphone</p>
                        <p className="text-xs text-muted-foreground mt-1">06 45 23 00 34</p>
                      </a>
                    </div>
                    <p className="text-sm italic text-muted-foreground/80">Ouvert du mardi au dimanche, de 10h à 20h. 65 Boulevard Westinghouse, Sevran.</p>
                  </div>
                ),
              },
              {
                q: 'Proposez-vous des formules et cartes cadeaux ?',
                a: (
                  <div className="space-y-4">
                    <p><strong>Oui !</strong> Deux façons d'optimiser votre expérience :</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Link to="/formules" onClick={() => window.scrollTo(0, 0)} className="rounded-xl bg-primary/5 border border-primary/10 p-4 hover:border-primary/30 transition-colors block">
                        <Sparkles size={20} className="text-primary mb-2" />
                        <p className="font-heading font-bold text-sm text-foreground">Formules combinées</p>
                        <p className="text-xs text-muted-foreground mt-1">Combinez plusieurs soins à tarif préférentiel. Dès 60 €.</p>
                      </Link>
                      <Link to="/offrir" onClick={() => window.scrollTo(0, 0)} className="rounded-xl bg-primary/5 border border-primary/10 p-4 hover:border-primary/30 transition-colors block">
                        <Heart size={20} className="text-primary mb-2" />
                        <p className="font-heading font-bold text-sm text-foreground">Cartes cadeaux</p>
                        <p className="text-xs text-muted-foreground mt-1">Offrez un moment de bien-être. PDF avec code unique.</p>
                      </Link>
                    </div>
                  </div>
                ),
              },
            ].map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-2xl px-6 md:px-8 bg-card data-[state=open]:border-primary/30 data-[state=open]:shadow-sm transition-all">
                <AccordionTrigger className="font-heading text-base md:text-lg font-semibold text-foreground hover:no-underline py-5">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-6 font-body">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link to="/faq" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base md:text-lg hover:underline">
              Voir toutes les questions <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Articles liés */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Blog</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Nos conseils bien-être & beauté</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto font-body">
              Retrouvez nos articles pour mieux comprendre les soins et choisir ceux qui vous conviennent.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {blogArticles
              .filter(a => ['Soins Visage', 'Soins Corps', 'Épilation'].includes(a.category))
              .slice(0, 4)
              .map((article, i) => (
                <BlogCard key={article.slug} article={article} index={i} />
              ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline">
              Tous nos articles <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Envie de prendre soin de vous ?"
        subtitle="Prenez rendez-vous en ligne et offrez-vous un moment de bien-être. Diagnostic personnalisé offert pour tout premier soin."
        buttonLabel="Réserver sur Planity"
        buttonLink={PLANITY_URL}
        isExternal
      />
    </>
  );
};

export default Services;
